import { Trans, useTranslation } from 'react-i18next';
import { ReactComponent as ArrowLeftIcon } from '@Assets/dnbEmbeddedIntro/carousel-arrow-left.svg';
import { ReactComponent as ArrowRightIcon } from '@Assets/dnbEmbeddedIntro/carousel-arrow-right.svg';
import { ReactComponent as PauseIcon } from '@Assets/dnbEmbeddedIntro/carousel-pause.svg';
import { ReactComponent as PlayIcon } from '@Assets/dnbEmbeddedIntro/carousel-play.svg';
import { useDnbCarouselLogic } from './useDnbCarouselLogic';
import { DNB_CAROUSEL_SURFACE } from './dnbCarousel.consts';

interface DnbCarouselProps {
  // The tablet reuses the compact (mobile-style) illustration compositions; desktop has its own.
  variant?: 'desktop' | 'tablet';
}

export const DnbCarousel = ({ variant = 'desktop' }: DnbCarouselProps) => {
  const isTablet = variant === 'tablet';
  const { t } = useTranslation();
  const { slides, activeIndex, isPlaying, progress, handleNext, handlePrev, togglePlaying } =
    useDnbCarouselLogic({ withProgress: true });

  return (
    <div
      className='flex flex-col items-center justify-center w-full h-full max-w-[777px] max-h-[676px] rounded-[12px] overflow-hidden'
      style={{ background: DNB_CAROUSEL_SURFACE }}>
      <div className='grid w-full pt-[88px] px-[24px]'>
        {slides.map((slide, index) => (
          <p
            key={slide.kind}
            className={`col-start-1 row-start-1 w-full max-w-[400px] mx-auto text-center text-[18px] leading-[24px] tracking-[-0.2px] text-text-main-primary-inverse transition-opacity duration-[550ms] ${index === activeIndex ? 'opacity-100' : 'opacity-0'}`}>
            <Trans
              i18nKey={`pages.dnbEmbeddedIntro.carousel.${slide.kind}Title`}
              components={{ sup: <sup /> }}
            />
          </p>
        ))}
      </div>
      <div className='relative flex flex-1 items-center justify-center w-full min-h-0'>
        {slides.map((slide, index) => (
          <div
            key={slide.kind}
            className={`absolute inset-0 flex items-center justify-center transition-opacity duration-[550ms] ${index === activeIndex ? 'opacity-100' : 'opacity-0'}`}>
            <img
              src={isTablet ? slide.compactImage : slide.image}
              alt={t(`pages.dnbEmbeddedIntro.carousel.${slide.kind}Alt`)}
              width={isTablet ? slide.tabletWidth : slide.width}
              height={isTablet ? slide.tabletHeight : slide.height}
              className={'max-w-[75%] max-h-[80%] object-contain'}
            />
          </div>
        ))}
      </div>
      <div className='flex flex-col items-start w-full p-[24px]'>
        <div className='flex items-center gap-[24px] w-full h-[44px]'>
          <button
            type='button'
            aria-label={t('pages.dnbEmbeddedIntro.carousel.previousSlide')}
            onClick={handlePrev}
            className='shrink-0 flex items-center justify-center size-[24px] text-text-main-primary-inverse'>
            <ArrowLeftIcon />
          </button>
          <div className='flex flex-1 items-center gap-[6px] h-[4px]'>
            {slides.map((slide, index) => (
              <div
                key={slide.kind}
                className='flex-1 h-full rounded-[2px] bg-white/[0.22] overflow-hidden'>
                <div
                  className='h-full rounded-[2px] bg-white'
                  style={{
                    width: `${index < activeIndex ? 100 : index > activeIndex ? 0 : progress * 100}%`,
                  }}
                />
              </div>
            ))}
          </div>
          <button
            type='button'
            aria-label={t('pages.dnbEmbeddedIntro.carousel.nextSlide')}
            onClick={handleNext}
            className='shrink-0 flex items-center justify-center size-[24px] text-text-main-primary-inverse'>
            <ArrowRightIcon />
          </button>
          <button
            type='button'
            aria-label={
              isPlaying
                ? t('pages.dnbEmbeddedIntro.carousel.pause')
                : t('pages.dnbEmbeddedIntro.carousel.play')
            }
            onClick={togglePlaying}
            className='shrink-0 flex items-center justify-center size-[24px] text-text-main-primary-inverse'>
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DnbCarousel;
